/**
 * deviceUrl.js — the one place that knows how to read `Companies.SocketioDeviceURL`.
 *
 * known-gaps #3, Stage 1 (storalytic-workspace/contracts/known-gaps.md).
 *
 * WHY THIS EXISTS
 * ---------------
 * The browser->device path is moving behind nginx on the portal's own origin:
 *
 *   LEGACY  https://portal.storalytic.io:5000          (direct to the device port)
 *   NEW     https://portal.storalytic.io/device/<id>   (proxied, same-origin, authed)
 *
 * Stage 2 cuts over ONE STORE AT A TIME by editing that store's row, so BOTH
 * shapes are live simultaneously for as long as the rollout takes. Every call
 * site therefore has to handle both — and if each one does that itself, the
 * cutover stops being a database change and becomes a code change. Putting the
 * knowledge here is what keeps Stage 2 to "flip one row, verify, flip the next".
 *
 * THE SOCKET.IO TRAP THIS ALSO FIXES
 * ----------------------------------
 * `io("https://portal.storalytic.io/device/5")` does NOT connect to a server at
 * that path. socket.io reads the path as a NAMESPACE, so it would ask for
 * namespace `/device/5` over the default `/socket.io` path — a namespace the edge
 * does not define, on a path nginx does not route. The call has to be split into
 * `io(origin, { path: socketPath })`, which is exactly what `socketPath` is for.
 *
 * `proxied` REPORTS WHICH SHAPE, AND THAT GOVERNS CREDENTIALS
 * -----------------------------------------------------------
 * Only the NEW shape is same-origin with the portal, and therefore only the new
 * shape may carry credentials. See `withCredentials` note at the bottom.
 */

const SOCKET_IO_PATH = '/socket.io'

const EMPTY = Object.freeze({
    origin: '',
    socketPath: SOCKET_IO_PATH,
    httpBase: '',
    proxied: false,
})

function stripTrailingSlashes(value) {
    return value.replace(/\/+$/, '')
}

/**
 * Split a stored SocketioDeviceURL into the parts each kind of call needs.
 *
 * @param {string} raw  the value straight out of `companyStore.company.SocketioDeviceURL`
 * @returns {{origin: string, socketPath: string, httpBase: string, proxied: boolean}}
 *
 *   origin      what to hand `io()` as its first argument
 *   socketPath  what to pass as socket.io's `path` option
 *   httpBase    prefix for device HTTP routes; concatenate "/check_connection"
 *               etc. directly onto it — it never ends in a slash
 *   proxied     true for the NEW nginx-proxied shape, false for the LEGACY
 *               direct-to-port shape
 *
 * LEGACY IS DELIBERATELY PASSED THROUGH VERBATIM. For the legacy shape `origin`
 * and `httpBase` are the stored string itself (minus any trailing slash), NOT a
 * re-serialisation of a parsed URL. That is not laziness — re-serialising can
 * quietly normalise things (a default port dropped, a host lowercased), and
 * while no store is cut over yet this function must reproduce today's behaviour
 * EXACTLY. Handing back the original string is the only way to guarantee that.
 *
 * Anything unparseable is treated as legacy for the same reason: whatever the
 * old code did with that value, it keeps doing.
 */
export function parseDeviceUrl(raw) {
    const value = typeof raw === 'string' ? raw.trim() : ''
    if (!value) return EMPTY

    let parsed
    try {
        parsed = new URL(value)
    } catch (_) {
        const base = stripTrailingSlashes(value)
        return { origin: base, socketPath: SOCKET_IO_PATH, httpBase: base, proxied: false }
    }

    // '' for "https://host:5000" and "https://host:5000/"; "/device/5" for both
    // "https://host/device/5" and "https://host/device/5/".
    const prefix = stripTrailingSlashes(parsed.pathname)

    if (!prefix) {
        const base = stripTrailingSlashes(value)
        return { origin: base, socketPath: SOCKET_IO_PATH, httpBase: base, proxied: false }
    }

    return {
        origin: parsed.origin,
        socketPath: `${prefix}${SOCKET_IO_PATH}`,
        httpBase: `${parsed.origin}${prefix}`,
        proxied: true,
    }
}

/**
 * Whether a device call built from `raw` may carry credentials.
 *
 * DO NOT turn this into an unconditional `true`. It reads as belt-and-braces and
 * it is not: on the LEGACY shape the device is CROSS-ORIGIN (a different port is
 * a different origin), and the edge answers every request with
 * `Access-Control-Allow-Origin: *` and no `Access-Control-Allow-Credentials`
 * (APPLICAITE_EDGE backend/utils/app_factory.py). A browser REJECTS a wildcard
 * ACAO on a credentialed request outright — so sending credentials before a
 * store is cut over does not merely fail to help, it breaks that store's live
 * view, camera test, zone frame and acknowledge, all at once.
 *
 * On the NEW shape the device is same-origin with the portal, where the cookie
 * is sent regardless and this flag is a genuine no-op that documents the intent.
 *
 * It becomes unconditionally true on its own once every store is cut over, at
 * which point this function and its callers can collapse to a literal.
 */
export function deviceSendsCredentials(raw) {
    return parseDeviceUrl(raw).proxied
}

/*
 * ---------------------------------------------------------------------------
 * LIVE DATA VIA IOT HUB (B4a, storalytic-workspace/docs/iothub-arc-migration.md)
 * ---------------------------------------------------------------------------
 * Per store, `Companies.LiveTransport` decides where the LIVE SCREENS get their
 * real-time events (heartbeat, zone_counter, tripwire_crossing, action_triggered,
 * stream_status):
 *
 *   'tunnel' (default)  straight from the box:            <device URL>/socket.io
 *   'iothub'            from the portal's live relay:     <device URL>/live/socket.io
 *
 * The relay sends the same events with the same payloads, so a screen only swaps
 * the URL it hands to parseDeviceUrl(). It lives UNDER /device/<id>/ because the
 * device_session cookie has Path=/device/, so the existing login check applies.
 *
 * Only the live socket moves. Video, camera test, zone frame, acknowledge and the
 * buttons keep using `SocketioDeviceURL` (the tunnel) whatever this says.
 */
const LIVE_SUFFIX = '/live'

/**
 * The URL a live screen's socket should use for this company.
 *
 * Falls back to the stored device URL unless the store is on 'iothub' AND its URL is
 * the proxied /device/<id> shape: the relay is reachable only on the portal's origin,
 * so a legacy ":500x" store switched to 'iothub' by mistake keeps working as before.
 *
 * @param {{SocketioDeviceURL?: string, LiveTransport?: string}} company
 * @returns {string|null|undefined}
 */
export function liveDeviceUrl(company) {
    const raw = company?.SocketioDeviceURL
    if (company?.LiveTransport !== 'iothub') return raw
    const parts = parseDeviceUrl(raw)
    if (!parts.proxied) return raw
    return `${parts.httpBase}${LIVE_SUFFIX}`
}

/** True when `raw` (as returned by liveDeviceUrl) points at the live relay. */
export function isLiveRelayUrl(raw) {
    const parts = parseDeviceUrl(raw)
    return parts.proxied && parts.httpBase.endsWith(LIVE_SUFFIX)
}

/**
 * What a screen fetches to see whether its live source answers before it opens the
 * socket. The box answers on its root; the relay on `/status` (its root is not routed:
 * nginx's location is /device/<id>/live/, with the trailing slash).
 */
export function liveProbeUrl(raw) {
    const { httpBase } = parseDeviceUrl(raw)
    return isLiveRelayUrl(raw) ? `${httpBase}/status` : httpBase
}
